import { isUndefined } from 'lodash';
import { CanvasType, PosterType } from '@meitu/saas-editor-core/es/version-types';
import { checkTypeIsAiPPT } from '@meitu/saas-editor-ui/es/utils/stage';
import getStore from '../store/index';
import { checkImageEditor } from './common';
import { getQuery } from './query';

/**
 * 获取地址栏的统计参数
 */
export const getQueryTrackObj = () => {
    const query = getQuery();

    const trackObj = {
        专题ID: query.subtopicId,
        分类ID: query.categoryId,
        mode_id: query.modeId,
        scene_id: query.querySceneId,
        application_id: query.queryCategoryId,
        industry_id: query.queryIndustryId,
        format: query.queryFormat,
        style: query.queryStyle,
        color: query.queryColorRgb,
        rank_use: query.statSort,
        is_search: query.is_search || '0',
        query: query.query,
        class: query.class,
        scm: query.scm,
        entry: query.from || 'other',
        calendar_id: query.calendarId,
        calendar_name: query.calendarName,
    };

    return filterEmpty(trackObj);
};

/**
 * 过滤空值
 *
 * @param trackObj - 键值对对象
 */
export const filterEmpty = (trackObj: { [key: string]: unknown }) => {
    Object.keys(trackObj).forEach((key) => {
        const val = trackObj[key];
        if (isUndefined(val)) {
            delete trackObj[key];
        }
    });

    return trackObj;
};

/**
 * 收费素材 集合  用于埋点上报
 */
export const getMaterialTrackObj = () => {
    // 埋点文档 paid_material 规则
    // https://doc.weixin.qq.com/sheet/e3_ANQA1Qb3AFMrge4Eg4gTtKJbWMABh?scode=ACIAJAeGAAgtgVo6lhASkA_QZpAJM&tab=ttjmpv&journal_source=chat

    const allMaterialList = getStore().getState().svipWatermarkModel.materialList;
    const isVipTemplate = getStore().getState().svipWatermarkModel.isVipTemplate;
    const thresholdIdMap = getStore().getState().svipWatermarkModel.thresholdIdMap;
    const stageMaterialIdMap = getStore().getState().svipWatermarkModel.stageMaterialIdMap;
    const stageMaterialLabelMap = getStore().getState().svipWatermarkModel.stageMaterialLabelMap;

    const { id: recordId, material_id: recordTemplateId } = getStore().getState().recordModel.recordInfo;

    const initTemplateId = getStore().getState().templateModel.id;

    let paid_material = '';
    if (isVipTemplate) {
        paid_material = `material_1000_${initTemplateId}`;
        if (recordId && recordTemplateId) {
            paid_material = `material_1000_${recordTemplateId}`;
        }
    }

    const trackParams = allMaterialList.map((item) => item.saveTrackParam).filter((item) => item);
    if (paid_material) {
        trackParams.unshift(paid_material);
    }

    paid_material = trackParams.join(',');

    function flatten2DArray(arr: any[]) {
        return [].concat(...arr);
    }
    const stageMaterialIds = Array.from(new Set(flatten2DArray(Array.from(stageMaterialIdMap.values()))));
    const material_id: string[] = [];
    stageMaterialIds.forEach((id) => {
        const item = thresholdIdMap.get(`${id}`);
        if (item && item.typeName) {
            const label_id = stageMaterialLabelMap.get(item.id as number);
            material_id.push(
                `${item.type}_${item.typeId}_${item.id}_${Number(item.isVip)}_${
                    item.trackName
                }${item.format ? '_' + item.format : ''}_${label_id || null}`,
            );
        }
    });

    console.log('【vip素材列表】：', allMaterialList);
    console.log('paid_material => ', paid_material);
    console.log('thresholdIdMap => ', thresholdIdMap);
    console.log('stageMaterialIdMap => ', stageMaterialIdMap);
    console.log('material_id => ', material_id);
    return {
        paid_material,
        material_id: material_id.join(','),
    };
};

export enum Scene {
    AiPPT = 'aippt',
    AiPoster = 'aihb',
    Poster = 'hbsj',
    ImageEditor = 'tpbj',
}

// 当前场景场景埋点类型 AI PPT /AI 海报/ 常规海报设计
export const getCurrentScene = () => {
    const isImageEditor = checkImageEditor();
    const type = getStore().getState()?.recordModel.posterType;

    if (isImageEditor) {
        // 图片编辑
        return Scene.ImageEditor;
    }
    if (
        checkTypeIsAiPPT(type) ||
        window.location.pathname.indexOf('/ppt') === 0 ||
        window.location.host.includes('ppt.designkit.com') ||
        window.location.host.includes('ppt1.designkit.com') ||
        window.location.host.includes('ppt2.designkit.com') ||
        window.location.host.includes('ppt.designkit.cn') ||
        window.location.host.includes('ppt1.designkit.cn') ||
        window.location.host.includes('ppt2.designkit.cn')
    ) {
        // AI PPT
        return Scene.AiPPT;
    }
    if (type === PosterType.AiPoster || window.location.pathname.indexOf('/poster') === 0) {
        // AI海报首页、模板类型为AI海报
        return Scene.AiPoster;
    }
    // 海报设计
    return Scene.Poster;
};

export const MTCCDataScene = {
    [Scene.AiPPT]: '00119',
    [Scene.AiPoster]: '00103',
    [Scene.Poster]: '00101',
    [Scene.ImageEditor]: '00124',
};

// 成本分摊埋点 https://cf.meitu.com/confluence/pages/viewpage.action?pageId=416230853
export const MTCCData: Record<string, { positionKey: string; function?: string; position: { [key: string]: string } }> =
    {
        // AI 花字
        '/ai_figure_text/create_task': {
            positionKey: '',
            position: {
                default: '00123',
            },
        },
        // AI 照片 / AI 背景
        '/ai_material/create': {
            positionKey: 'picture_type',
            position: {
                '1': '00121', // AI 照片
                '2': '00122', // AI 背景
                default: '',
            },
        },
        // 单页PPT、文生图、文生文
        '/ppt/apply_item': {
            positionKey: 'type',
            position: {
                '3': '00127', // AI 图片(文生图)
                '4': '00126', // AI 文字(文生文)
                default: MTCCDataScene[Scene.AiPPT],
            },
        },
        // 生成完整 PPT
        '/ppt/apply': {
            positionKey: '',
            function: MTCCDataScene[Scene.AiPPT],
            position: {
                default: MTCCDataScene[Scene.AiPPT],
            },
        },
        // 生成 AI 海报
        '/ai_poster/formula_async': {
            positionKey: '',
            function: MTCCDataScene[Scene.AiPoster],
            position: {
                default: MTCCDataScene[Scene.AiPoster],
            },
        },
        '/v1/mtlab/image_manipulation_async': {
            positionKey: '',
            position: {
                default: '9110',
            },
        },
        '/v1/mtlab/image_expand_async': {
            positionKey: '',
            position: {
                default: '1215',
            },
        },
        // 抠图
        'plugin-matting': {
            positionKey: '',
            position: {
                default: '00102',
            },
        },
        // 变清晰
        'plugin-aiQuality': {
            positionKey: '',
            position: {
                default: '00106',
            },
        },
        // 无损改尺寸
        'plugin-aiUpScale': {
            positionKey: '',
            position: {
                default: '00111',
            },
        },
    };

// 获取（海报）模板类型
export const getTemplateType = (canvasType?: CanvasType) => {
    if (canvasType === CanvasType.PPT) {
        return PosterType.NormalPPT;
    }
    return PosterType.Poster;
};
